import React, { useState } from "react";
import Image from "next/image";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import useAuthToken from "@/hooks/useAuthToken";
import { InspectionType, BundleType } from "@/types/inspections";
import { UserService, CompanyServiceServiceInstance } from "@/services";
import { ServiceTag } from "@/types/company_services";
import { ServicePaypoint } from "@/components/pages/user/dashboard/service_paypoint/ServicePaypoint";
import { buildSignInUrl } from "@/utils/helpers/authReturn";
import { formatNumber } from "@/utils/formatNumber";

const inspectionImage =
  "https://res.cloudinary.com/wxaeoyjh/image/upload/v1785416493/ChatGPT_Image_Jul_30_2026_02_01_09_PM_dgwtdz.png";

interface PropertyInspectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyTitle: string;
  propertyAddress: string;
  propertyId: string;
  isRald: boolean;
}

const ServicePrice: React.FC<{
  isLoading: boolean;
  isError: boolean;
  amount: number | undefined;
}> = ({ isLoading, isError, amount }) => {
  if (isLoading) {
    return (
      <span className="inline-block h-4 w-14 rounded bg-slate-200 animate-pulse align-middle" />
    );
  }
  if (isError || amount === undefined) {
    return <span className="text-slate-400">—</span>;
  }
  return <>{formatNumber(amount)}</>;
};

export const PropertyInspectionModal: React.FC<
  PropertyInspectionModalProps
> = ({ isOpen, onClose, propertyId, isRald }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedType, setSelectedType] = useState<InspectionType>(
    isRald ? InspectionType.INSPECTION_REPORT : InspectionType.LIVE_INSPECTION,
  );
  const [paymentMethod, setPaymentMethod] = useState<"bundle" | "">("bundle");

  // State definitions
  const [showBundlePaypoint, setShowBundlePaypoint] = useState(false);
  const [showNonBundlePaypoint, setShowNonBundlePaypoint] = useState(false);
  const [showBundlePurchasePaypoint, setShowBundlePurchasePaypoint] =
    useState(false);

  const toggleNonBundlePaypoint = () => {
    setShowNonBundlePaypoint((prev) => !prev);
  };
  // Toggle function
  const toggleBundlePurchasePaypoint = () => {
    setShowBundlePurchasePaypoint((prev) => !prev);
  };

  const { user } = useAuthToken();
  const router = useRouter();

  const { data: bundleBalanceResponse } = useQuery({
    queryKey: ["bundle-balance-modal"],
    queryFn: () => UserService.getBundleBalance(),
    enabled: isOpen && !!user,
    retry: 1,
  });

  const {
    data: liveInspectionServices,
    isLoading: isLiveInspectionLoading,
    isError: isLiveInspectionError,
  } = useQuery({
    queryKey: ["live-inspection-services-modal"],
    queryFn: () =>
      CompanyServiceServiceInstance.getServicesByTag(
        ServiceTag.LIVE_INSPECTION,
      ),
    enabled: isOpen && !!user,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const {
    data: inspectionReportServices,
    isLoading: isInspectionReportLoading,
    isError: isInspectionReportError,
  } = useQuery({
    queryKey: ["inspection-report-services-modal"],
    queryFn: () =>
      CompanyServiceServiceInstance.getServicesByTag(
        ServiceTag.INSPECTION_REPORT,
      ),
    enabled: isOpen && !!user,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (!isOpen) return null;

  const liveWalkthroughService = liveInspectionServices?.data?.data?.[0];
  const isLiveInitialized =
    !!liveWalkthroughService && (liveWalkthroughService.amount ?? 0) > 0;

  const inspectionReportService = inspectionReportServices?.data?.data?.[0];
  const isReportInitialized =
    !!inspectionReportService && (inspectionReportService.amount ?? 0) > 0;

  const bundleData = bundleBalanceResponse?.data?.data;
  const availableSlots =
    selectedType === InspectionType.INSPECTION_REPORT
      ? (bundleData?.inspectionReportBundle ?? 0)
      : (bundleData?.liveInspectionReportBundle ?? 0);

  const price =
    selectedType === InspectionType.INSPECTION_REPORT
      ? (inspectionReportService?.amount ?? 0)
      : (liveWalkthroughService?.amount ?? 0);

  const bundleInfo = {
    [InspectionType.INSPECTION_REPORT]: {
      title: "Verified Inspection Report Bundle",
      description:
        "Purchase 5 verified inspection report slots and save 5%. Each slot can be used to request a detailed inspection report on any property listing — including structural integrity, utility checks, and legal status.",
      bundleType: "inspection-report-bundle" as BundleType,
    },
    [InspectionType.LIVE_INSPECTION]: {
      title: "Live Walkthrough Bundle",
      description:
        "Purchase 5 live walkthrough slots and save 5%. Each slot includes a real-time HD video tour with an expert inspector plus a full detailed report on the property.",
      bundleType: "live-inspection-bundle" as BundleType,
    },
  };

  const currentBundle = bundleInfo[selectedType];

  const handleProceed = () => {
    if (!user) {
      toast.error(
        "You need to login to complete your property inspection request.",
      );

      // Parse current query params and merge inspection=1 (preserves ?ref=social, etc.)
      const [pathname, search] = router.asPath.split("?");
      const params = new URLSearchParams(search);
      params.set("inspection", "1");

      const returnPath = `${pathname}?${params.toString()}`;
      router.push(buildSignInUrl(returnPath));
      return;
    }
    setStep(2);
  };

  const handleConfirmPayment = () => {
    if (paymentMethod === "bundle") {
      setShowBundlePaypoint(true);
    } else {
      toggleNonBundlePaypoint();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl overflow-y-auto  max-h-[85vh] w-full max-w-3xl shadow-2xl border border-slate-100 overflow-hidden transition-all my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-1 md:py-3 md:px-8">
          <h2 className="text-base font-bold tracking-[-0.5px] text-slate-800">
            {step === 1 ? "Property inspection Overview" : "Confirm & Pay"}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-all text-sm"
          >
            ✕
          </button>
        </div>

        {/* Step 1: Info & Selection */}
        {step === 1 && (
          <div className="space-y-8 p-4">
            {/* ================= HERO SECTION ================= */}
            <div className="rounded-xl p-0">
              {/* Mobile Layout */}
              <div className="block sm:hidden space-y-5">
                <h3 className="font-semibold text-[17px] tracking-[-0.8px] text-slate-900">
                  You can have a trusted expert inspect any property
                </h3>

                <div className="relative w-full aspect-square overflow-hidden rounded-xl bg-slate-50">
                  <Image
                    src={inspectionImage}
                    alt="live-inspection"
                    fill
                    className="object-cover"
                  />
                </div>

                <p className="text-sm text-slate-600 leading-relaxed">
                  Are you interested in purchasing or renting a property but
                  can&apos;t be there in person to inspect it? Whether
                  you&apos;re overseas or have a busy schedule, our Live
                  Inspection Request service ensures that you don&apos;t miss
                  out on the critical details of your property search.
                </p>

                <p className="text-sm text-slate-600 leading-relaxed">
                  Our expert team will visit the property on your behalf,
                  conduct a thorough inspection, and provide you with a
                  detailed, comprehensive report. Through high-quality photos,
                  videos, and other report materials, you&apos;ll get a
                  first-hand look at the property — just as if you were there
                  yourself.
                </p>
              </div>

              {/* Desktop Layout */}
              <div className="hidden sm:flex gap-8 items-center p-4 border border-slate-100 rounded-100 ">
                <div className="relative w-full h-[350px] max-w-[300px]">
                  <Image
                    src={inspectionImage}
                    alt="live-inspection"
                    fill
                    className="object-cover"
                  />
                </div>

                <div className="flex-1 space-y-4">
                  <h3 className="font-semibold text-[17px] tracking-[-0.8px] text-slate-900">
                    You can have a trusted expert inspect any property
                  </h3>

                  <p className="text-sm text-slate-600 leading-relaxed">
                    Are you interested in purchasing or renting a property but
                    can&apos;t be there in person to inspect it? Whether
                    you&apos;re overseas or have a busy schedule, our Live
                    Inspection Request service ensures that you don&apos;t miss
                    out on the critical details of your property search.
                  </p>

                  <p className="text-sm text-slate-600 leading-relaxed">
                    Our expert team will visit the property on your behalf,
                    conduct a thorough inspection, and provide you with a
                    detailed, comprehensive report. Through high-quality photos,
                    videos, and other report materials, you&apos;ll get a
                    first-hand look at the property — just as if you were there
                    yourself.
                  </p>
                </div>
              </div>
            </div>

            {/* ================= SERVICE OPTIONS ================= */}
            <div className="p-4 border border-slate-100 rounded-100 space-y-4">
              {/* Option 1 */}
              {isRald ? (
                <div
                  onClick={() =>
                    setSelectedType(InspectionType.INSPECTION_REPORT)
                  }
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-start justify-between ${
                    selectedType === InspectionType.INSPECTION_REPORT
                      ? "border-[#2d96a7] bg-[#2d96a7]/5"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="pr-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-sm text-slate-900">
                        Verified Inspection Report
                      </h4>

                      <span className="hidden text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-md font-bold">
                        Most Popular
                      </span>
                    </div>

                    <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                      An expert visits the location and delivers a detailed
                      document covering structural integrity, utility checks,
                      photos, and legal/safety status.
                    </p>
                  </div>

                  <div className="shrink-0 text-right">
                    <p className="font-bold text-sm text-slate-900">
                      ₦
                      <ServicePrice
                        isLoading={isInspectionReportLoading}
                        isError={isInspectionReportError}
                        amount={inspectionReportService?.amount}
                      />
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() =>
                    setSelectedType(InspectionType.LIVE_INSPECTION)
                  }
                  className={`hidden p-4 rounded-xl border-2 cursor-pointer transition-all flex items-start justify-between ${
                    selectedType === InspectionType.LIVE_INSPECTION
                      ? "border-[#2d96a7] bg-[#2d96a7]/5"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="pr-4">
                    <h4 className="font-bold text-sm text-slate-900">
                      Live Streamed Walkthrough + Report
                    </h4>

                    <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                      Includes everything in the report plus a scheduled
                      real-time HD video tour with the agent/inspector so you
                      can direct where they look live.
                    </p>
                  </div>

                  <div className="shrink-0 text-right">
                    <p className="font-bold text-sm text-slate-900">
                      ₦
                      <ServicePrice
                        isLoading={isLiveInspectionLoading}
                        isError={isLiveInspectionError}
                        amount={liveWalkthroughService?.amount}
                      />
                    </p>
                  </div>
                </div>
              )}
            </div>

            {selectedType === InspectionType.INSPECTION_REPORT &&
              inspectionReportServices &&
              !isReportInitialized && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  Inspection report service hasn&apos;t been initialized by
                  company admin.
                </p>
              )}

            {selectedType === InspectionType.LIVE_INSPECTION &&
              liveInspectionServices &&
              !isLiveInitialized && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  Live walkthrough service hasn&apos;t been initialized by
                  company admin.
                </p>
              )}

            <button
              onClick={handleProceed}
              disabled={
                (selectedType === InspectionType.INSPECTION_REPORT &&
                  inspectionReportServices &&
                  !isReportInitialized) ||
                (selectedType === InspectionType.LIVE_INSPECTION &&
                  liveInspectionServices &&
                  !isLiveInitialized)
              }
              className="w-full py-3.5 bg-[#2d96a7] hover:bg-[#2d96a7]/90 text-white text-sm font-semibold rounded-xl transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              I understand, let&apos;s go
            </button>
          </div>
        )}
        {/* Step 2: Confirm & Pay */}
        {step === 2 && (
          <div className="p-6 space-y-6">
            {/* Summary */}
            <div className="flex justify-between items-center bg-slate-50 p-4 rounded-xl border border-slate-100">
              <div>
                <p className="text-xs text-slate-500 tracking-[-0.85px] font-medium">
                  You Selected:
                </p>
                <p className="text-sm font-bold text-slate-900 capitalize">
                  {selectedType.replace("_", " ")} Service
                </p>
              </div>
              <p className="text-lg font-black text-[#2d96a7]">
                ₦
                <ServicePrice
                  isLoading={
                    selectedType === InspectionType.INSPECTION_REPORT
                      ? isInspectionReportLoading
                      : isLiveInspectionLoading
                  }
                  isError={
                    selectedType === InspectionType.INSPECTION_REPORT
                      ? isInspectionReportError
                      : isLiveInspectionError
                  }
                  amount={price}
                />
              </p>
            </div>

            {/* Divider / Section Header */}
            <div className="relative my-5">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-start">
                <span className="bg-white pr-2 text-xs font-medium text-slate-700">
                  Proceed to Payment
                </span>
              </div>
            </div>

            {/* Primary Payment: Bundle Option */}
            <div className="rounded-lg border border-slate-200/80 bg-slate-50/50 p-4 transition-all">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-900 tracking-tight">
                      {currentBundle.title}
                    </h3>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-[#2d96a7]/10 text-[#2d96a7]">
                      Bundle Plan
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {currentBundle.description}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-200/60 flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="text-slate-500 font-normal">
                    Available Balance:
                  </span>
                  <span
                    className={`font-semibold ${availableSlots > 0 ? "text-slate-900" : "text-amber-700"}`}
                  >
                    {availableSlots}{" "}
                    {availableSlots === 1 ? "inspection" : "inspections"}
                  </span>
                </div>

                {availableSlots === 0 && (
                  <button
                    type="button"
                    onClick={toggleBundlePurchasePaypoint}
                    className="h-9 px-4 inline-flex items-center justify-center rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold transition-colors"
                  >
                    Buy Bundle
                  </button>
                )}
              </div>
            </div>

            {/* Divider / Section Header */}
            <div className="relative my-5">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-start">
                <span className="bg-white pr-2 text-xs font-medium text-slate-700">
                  Proceed to Payment
                </span>
              </div>
            </div>
            {/* Secondary Payment Options */}
            <div className="space-y-3">
              {/* Bundle Option */}
              <label
                className={`group relative flex items-center justify-between p-3.5 rounded-xl border text-left transition-all duration-200 ${
                  availableSlots === 0
                    ? "opacity-60 cursor-not-allowed bg-slate-50/60 border-slate-200"
                    : paymentMethod === "bundle"
                      ? "border-[#2d96a7] bg-[#2d96a7]/[0.03] ring-1 ring-[#2d96a7] cursor-pointer"
                      : "border-slate-200/80 hover:border-slate-300 bg-white shadow-xs cursor-pointer"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="payment"
                    disabled={availableSlots === 0}
                    checked={paymentMethod === "bundle"}
                    onChange={() => setPaymentMethod("bundle")}
                    className="h-4 w-4 border-slate-300 text-[#2d96a7] focus:ring-0 focus:ring-offset-0 accent-[#2d96a7] disabled:cursor-not-allowed"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-slate-900">
                        Pay with Bundle
                      </p>
                      <span className="inline-flex items-center rounded-md bg-[#2d96a7]/10 px-1.5 py-0.5 text-[10px] font-medium text-[#2d96a7]">
                        {availableSlots > 0
                          ? `${availableSlots} slot${availableSlots > 1 ? "s" : ""} left`
                          : "No inspection report left"}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      This will deduct your inspection report bundle balance
                    </p>
                  </div>
                </div>
              </label>

              {/* Card / Wallet Option */}
              <label
                className={`group relative flex items-center justify-between p-3.5 rounded-xl border text-left cursor-pointer transition-all duration-200 ${
                  paymentMethod === ""
                    ? "border-[#2d96a7] bg-[#2d96a7]/[0.03] ring-1 ring-[#2d96a7]"
                    : "border-slate-200/80 hover:border-slate-300 bg-white shadow-xs"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="payment"
                    checked={paymentMethod === ""}
                    onChange={() => setPaymentMethod("")}
                    className="h-4 w-4 border-slate-300 text-[#2d96a7] focus:ring-0 focus:ring-offset-0 cursor-pointer accent-[#2d96a7]"
                  />
                  <div>
                    <p className="text-xs font-semibold text-slate-900">
                      Pay via card or wallet
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Pay ₦
                      <ServicePrice
                        isLoading={
                          selectedType === InspectionType.INSPECTION_REPORT
                            ? isInspectionReportLoading
                            : isLiveInspectionLoading
                        }
                        isError={
                          selectedType === InspectionType.INSPECTION_REPORT
                            ? isInspectionReportError
                            : isLiveInspectionError
                        }
                        amount={price}
                      />{" "}
                      with a debit/credit card or wallet
                    </p>
                  </div>
                </div>
              </label>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep(1)}
                className="w-1/3 py-3 border border-slate-200 text-slate-700 text-sm font-bold rounded-xl hover:bg-slate-50 transition-all"
              >
                Back
              </button>
              <button
                onClick={handleConfirmPayment}
                className="w-2/3 py-3 bg-[#2d96a7] hover:bg-[#2d96a7]/90 text-white text-sm font-bold rounded-xl transition-all shadow-sm"
              >
                Confirm Request
              </button>
            </div>
          </div>
        )}
      </div>

      {showBundlePaypoint && (
        <ServicePaypoint
          service_units={1}
          service_id={
            selectedType === InspectionType.INSPECTION_REPORT
              ? (inspectionReportService?.service_id ?? "")
              : (liveWalkthroughService?.service_id ?? "")
          }
          target_resource_id={propertyId}
          returnUrl={router.asPath}
          onClose={() => setShowBundlePaypoint(false)}
          isBundlePayment={true}
          paymentPurpose={
            selectedType === InspectionType.INSPECTION_REPORT
              ? "inspection_report"
              : "live_inspection"
          }
        />
      )}

      {showBundlePurchasePaypoint && (
        <ServicePaypoint
          service_units={
            selectedType === InspectionType.INSPECTION_REPORT
              ? (inspectionReportService?.discount_units_threshold ?? 5)
              : (liveWalkthroughService?.discount_units_threshold ?? 5)
          }
          service_id={
            selectedType === InspectionType.INSPECTION_REPORT
              ? (inspectionReportService?.service_id ?? "inspection-report")
              : (liveWalkthroughService?.service_id ?? "live-inspection")
          }
          target_resource_id={propertyId}
          returnUrl={router.asPath}
          onClose={() => setShowBundlePurchasePaypoint(false)}
        />
      )}

      {showNonBundlePaypoint && (
        <ServicePaypoint
          service_units={1}
          service_id={
            selectedType === InspectionType.INSPECTION_REPORT
              ? (inspectionReportService?.service_id ?? "")
              : (liveWalkthroughService?.service_id ?? "")
          }
          target_resource_id={propertyId}
          returnUrl={router.asPath}
          onClose={() => setShowNonBundlePaypoint(false)}
          paymentPurpose={
            selectedType === InspectionType.INSPECTION_REPORT
              ? "inspection_report"
              : "live_inspection"
          }
        />
      )}
    </div>
  );
};
